import {
  ProductStatusSchema,
  ProductTypeFilterSchema
} from "../chunk-DTQF5GFJ.js";
import {
  PortalUserRoleSchema
} from "../chunk-7PF4POI6.js";
import {
  GENERAL_MERCHANDISE_CATEGORIES,
  PRODUCT_REALMS,
  ProductRealmSchema,
  REALM_COMPLIANCE_DEFAULTS,
  REALM_FULFILLMENT_DEFAULTS,
  RealmComplianceConfigSchema,
  RealmContextSchema,
  realmRequiresCompliance,
  resolveOrgBrandFulfillment
} from "../chunk-HJPR3SS7.js";

// product/product-management.schema.ts
import { z } from "zod";
var ImageContentTypeSchema = z.enum(["image/jpeg", "image/png", "image/gif"]);
var CreateProductVariantInputSchema = z.object({
  title: z.string().min(1, "Variant title is required").max(255),
  sku: z.string().max(100).optional(),
  barcode: z.string().max(100).optional(),
  unitWeight: z.number().positive().optional(),
  unitsPerCase: z.number().int().positive().optional(),
  price: z.number().nonnegative().optional(),
  // METRC fields (when isMetrcTracked on the product)
  metrcItemId: z.number().int().optional(),
  metrcItemName: z.string().optional(),
  metrcCategoryName: z.string().optional(),
  metrcStrainName: z.string().optional(),
  metrcUnitOfMeasure: z.string().optional()
});
var CreateProductDocumentInputSchema = z.object({
  title: z.string().min(1).max(255),
  filename: z.string().min(1).max(255),
  contentType: z.string(),
  fileData: z.string()
  // base64
});
var CreateProductInputSchema = z.object({
  // Product info
  name: z.string().min(1, "Product name is required").max(255),
  brandId: z.string().uuid("Invalid brand ID"),
  categoryId: z.string().uuid().optional(),
  description: z.string().max(5e3).optional(),
  isMetrcTracked: z.boolean(),
  // Variants (1 or more)
  variants: z.array(CreateProductVariantInputSchema).min(1, "At least one variant is required"),
  // Image upload (base64 encoded) — single image (backward compat)
  imageData: z.string().optional(),
  imageFileName: z.string().max(255).optional(),
  imageContentType: ImageContentTypeSchema.optional(),
  // Multi-image upload (base64 encoded) — takes precedence over single image fields when provided
  images: z.array(z.object({
    data: z.string(),
    fileName: z.string().max(255),
    contentType: ImageContentTypeSchema
  })).max(12).optional(),
  // Compliance documents (COAs, lab reports, labels)
  documents: z.array(CreateProductDocumentInputSchema).optional(),
  // License context for METRC API calls
  licenseNumber: z.string().optional(),
  stockLocationId: z.string().uuid().optional()
});
var CreatedProductVariantDtoSchema = z.object({
  id: z.string().uuid(),
  title: z.string(),
  sku: z.string().nullable(),
  barcode: z.string().nullable(),
  requiresMetrcTracking: z.boolean(),
  metrcItemId: z.number().int().nullable(),
  metrcUnitWeight: z.number().nullable(),
  metrcUnitsPerCase: z.number().int().nullable(),
  createdAt: z.string().datetime()
});
var CreatedProductDtoSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  status: z.string(),
  brandId: z.string().uuid(),
  categoryId: z.string().uuid().nullable(),
  isMetrcTracked: z.boolean(),
  thumbnail: z.string().nullable(),
  externalId: z.string().nullable(),
  variants: z.array(CreatedProductVariantDtoSchema),
  createdAt: z.string().datetime()
});
var ProductCreationErrorSchema = z.object({
  message: z.string(),
  code: z.string().optional()
});
var BulkCreateProductsInputSchema = z.object({
  products: z.array(CreateProductInputSchema).min(1, "At least one product is required").max(50, "Maximum 50 products per batch")
});
var BulkCreatedProductSummarySchema = z.object({
  id: z.string().uuid(),
  name: z.string()
});
var BulkCreateProductResultSchema = z.object({
  index: z.number(),
  success: z.boolean(),
  product: BulkCreatedProductSummarySchema.optional(),
  error: z.object({ message: z.string(), code: z.string().optional() }).optional()
});
var BulkCreateProductsResponseSchema = z.object({
  results: z.array(BulkCreateProductResultSchema),
  summary: z.object({
    total: z.number(),
    created: z.number(),
    failed: z.number()
  })
});
var LinkedMetrcItemIdsResponseSchema = z.object({
  metrcItemIds: z.array(z.number())
});
var ProductVariantDetailDtoSchema = z.object({
  id: z.string().uuid(),
  title: z.string(),
  sku: z.string().nullable(),
  barcode: z.string().nullable(),
  variantRank: z.number().int(),
  requiresMetrcTracking: z.boolean(),
  metrcItemId: z.number().int().nullable(),
  metrcUnitWeight: z.number().nullable(),
  metrcUnitsPerCase: z.number().int().nullable(),
  metadata: z.record(z.unknown()).nullable(),
  price: z.number().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime()
});
var ProductVariantsResponseSchema = z.object({
  productId: z.string().uuid(),
  variants: z.array(ProductVariantDetailDtoSchema)
});
var UpdateProductVariantInputSchema = z.object({
  title: z.string().min(1).max(255).optional(),
  sku: z.string().max(100).nullable().optional(),
  barcode: z.string().max(100).nullable().optional(),
  variantRank: z.number().int().min(0).optional(),
  requiresMetrcTracking: z.boolean().optional(),
  metrcUnitWeight: z.number().positive().nullable().optional(),
  metrcUnitsPerCase: z.number().int().positive().nullable().optional(),
  price: z.number().nonnegative().nullable().optional()
});
var UpdateProductVariantResponseSchema = z.object({
  id: z.string().uuid(),
  title: z.string(),
  sku: z.string().nullable(),
  barcode: z.string().nullable(),
  price: z.number().nullable(),
  updatedAt: z.string().datetime()
});
var AddProductVariantInputSchema = z.object({
  title: z.string().min(1, "Variant title is required").max(255),
  sku: z.string().max(100).optional(),
  barcode: z.string().max(100).optional(),
  requiresMetrcTracking: z.boolean().optional(),
  metrcItemId: z.number().int().optional(),
  metrcItemName: z.string().optional(),
  metrcCategoryName: z.string().optional(),
  metrcStrainName: z.string().optional(),
  metrcUnitOfMeasure: z.string().optional(),
  metrcUnitWeight: z.number().positive().optional(),
  metrcUnitsPerCase: z.number().int().positive().optional(),
  price: z.number().nonnegative().optional()
});
var AddProductVariantResponseSchema = ProductVariantDetailDtoSchema;
var DeleteProductVariantResponseSchema = z.object({
  success: z.boolean(),
  deletedVariantId: z.string().uuid()
});
var AddProductImageInputSchema = z.object({
  imageData: z.string(),
  imageFileName: z.string().max(255),
  imageContentType: ImageContentTypeSchema,
  altText: z.string().max(500).optional()
});
var ReorderProductGalleryInputSchema = z.object({
  order: z.array(z.object({
    imageId: z.string().uuid(),
    rank: z.number().int().min(0)
  })).min(1)
});
var DeleteProductGalleryImageResponseSchema = z.object({
  success: z.boolean(),
  deletedImageId: z.string().uuid()
});

// product/product-images.schema.ts
import { z as z2 } from "zod";
var ImageSourceSchema = z2.enum(["external", "local", "synced"]);
var ProductImageDtoSchema = z2.object({
  id: z2.string().uuid(),
  productId: z2.string().uuid(),
  url: z2.string().url(),
  altText: z2.string().max(500).nullable(),
  rank: z2.number().int().min(0),
  metadata: z2.record(z2.unknown()).default({}),
  // Storage fields for Supabase migration
  storagePath: z2.string().nullable(),
  storageUrl: z2.string().url().nullable(),
  storageProvider: z2.enum(["supabase", "r2", "s3"]).nullable().optional(),
  storageBucket: z2.string().nullable().optional(),
  syncedAt: z2.string().datetime().nullable(),
  source: ImageSourceSchema,
  // Timestamps
  createdAt: z2.string().datetime(),
  updatedAt: z2.string().datetime()
});
var CreateProductImageInputSchema = z2.object({
  productId: z2.string().uuid(),
  url: z2.string().url(),
  altText: z2.string().max(500).optional(),
  rank: z2.number().int().min(0).optional(),
  metadata: z2.record(z2.unknown()).optional(),
  // Optional storage fields for Supabase Storage
  storagePath: z2.string().optional(),
  storageUrl: z2.string().url().optional(),
  storageProvider: z2.enum(["supabase", "r2", "s3"]).optional(),
  storageBucket: z2.string().optional(),
  source: ImageSourceSchema.optional().default("external")
});
var UpdateStorageInfoInputSchema = z2.object({
  storagePath: z2.string(),
  storageUrl: z2.string().url(),
  storageProvider: z2.enum(["supabase", "r2", "s3"]).optional(),
  storageBucket: z2.string().optional(),
  syncedAt: z2.date(),
  source: ImageSourceSchema
});
var UpdateProductImageInputSchema = z2.object({
  url: z2.string().url().optional(),
  altText: z2.string().max(500).nullable().optional(),
  rank: z2.number().int().min(0).optional(),
  metadata: z2.record(z2.unknown()).optional()
});
var ReorderProductImagesInputSchema = z2.object({
  productId: z2.string().uuid(),
  imageIds: z2.array(z2.string().uuid()).min(1)
});
var ProductImagesListResponseSchema = z2.object({
  images: z2.array(ProductImageDtoSchema),
  total: z2.number().int()
});

// product/product-listing.schema.ts
import { z as z3 } from "zod";
var SupplierProductListingDtoSchema = z3.object({
  id: z3.string().uuid(),
  title: z3.string(),
  description: z3.string().nullable(),
  sku: z3.string().nullable(),
  isSample: z3.boolean(),
  thumbnail: z3.string().nullable(),
  status: ProductStatusSchema,
  brandId: z3.string().uuid().nullable(),
  brandName: z3.string().nullable(),
  brandRealm: ProductRealmSchema.optional(),
  isMetrcTracked: z3.boolean(),
  createdAt: z3.string().datetime(),
  updatedAt: z3.string().datetime()
});
var SupplierProductListingResponseSchema = z3.object({
  products: z3.array(SupplierProductListingDtoSchema),
  total: z3.number().int(),
  page: z3.number().int(),
  pageSize: z3.number().int(),
  hasMore: z3.boolean()
});
var ProductLocationInventoryDtoSchema = z3.object({
  locationId: z3.string().uuid(),
  locationName: z3.string(),
  locationAddress: z3.object({
    address1: z3.string(),
    city: z3.string().nullable(),
    province: z3.string().nullable()
  }).nullable(),
  canView: z3.boolean(),
  canEdit: z3.boolean(),
  inventoryLevelId: z3.string().uuid(),
  stockedQuantity: z3.number().int(),
  reservedQuantity: z3.number().int(),
  availableQuantity: z3.number().int(),
  updatedAt: z3.string().datetime()
});
var ProductInventorySummarySchema = z3.object({
  totalLocations: z3.number().int(),
  totalOnHand: z3.number().int(),
  totalReserved: z3.number().int(),
  totalAvailable: z3.number().int(),
  lowStockLocations: z3.number().int(),
  outOfStockLocations: z3.number().int()
});
var ProductInventoryResponseSchema = z3.object({
  product: SupplierProductListingDtoSchema,
  inventoryByLocation: z3.array(ProductLocationInventoryDtoSchema),
  summary: ProductInventorySummarySchema,
  images: z3.array(ProductImageDtoSchema).optional()
});
var ProductReservationDtoSchema = z3.object({
  id: z3.string().uuid(),
  trackingId: z3.string(),
  orderPartitionId: z3.string().uuid(),
  reservedQuantity: z3.number().int(),
  orderedQuantity: z3.number().int(),
  fulfillmentStatus: z3.string(),
  preparationStatus: z3.string(),
  stockLocationId: z3.string().uuid().nullable(),
  stockLocationName: z3.string().nullable(),
  customerName: z3.string().nullable(),
  companyName: z3.string().nullable(),
  reservedAt: z3.string().datetime().nullable(),
  createdAt: z3.string().datetime()
});
var ProductReservationsResponseSchema = z3.object({
  reservations: z3.array(ProductReservationDtoSchema),
  totalReserved: z3.number().int()
});
var AdminProductListingDtoSchema = SupplierProductListingDtoSchema.extend({
  supplierId: z3.string().uuid().nullable(),
  supplierName: z3.string().nullable()
});
var AdminProductListingFiltersSchema = z3.object({
  status: ProductStatusSchema.optional(),
  search: z3.string().optional(),
  supplierId: z3.string().uuid().optional(),
  brandId: z3.string().uuid().optional(),
  limit: z3.number().int().optional(),
  offset: z3.number().int().optional()
});
var AdminProductListingResponseSchema = z3.object({
  items: z3.array(AdminProductListingDtoSchema),
  total: z3.number().int()
});

// product/product-files.schema.ts
import { z as z4 } from "zod";
var FileSourceSchema = z4.enum(["external", "synced"]);
var ProductFileDtoSchema = z4.object({
  id: z4.string().uuid(),
  externalId: z4.number().int(),
  // B2BWave file ID
  originalUrl: z4.string().url(),
  // B2BWave hosted URL
  urlHash: z4.string(),
  // SHA256 hash for deduplication
  filename: z4.string(),
  title: z4.string(),
  // Storage fields (populated after upload to Supabase)
  storagePath: z4.string().nullable(),
  storageUrl: z4.string().url().nullable(),
  storageProvider: z4.enum(["supabase", "r2", "s3"]).nullable().optional(),
  storageBucket: z4.string().nullable().optional(),
  contentType: z4.string().nullable(),
  fileSize: z4.number().int().nullable(),
  // Sync tracking
  source: FileSourceSchema,
  syncedAt: z4.string().datetime().nullable(),
  // Timestamps
  createdAt: z4.string().datetime(),
  updatedAt: z4.string().datetime()
});
var UpsertProductFileInputSchema = z4.object({
  externalId: z4.number().int(),
  originalUrl: z4.string().url(),
  urlHash: z4.string(),
  filename: z4.string(),
  title: z4.string()
});
var UpdateFileStorageInfoInputSchema = z4.object({
  storagePath: z4.string(),
  storageUrl: z4.string().url(),
  storageProvider: z4.enum(["supabase", "r2", "s3"]).optional(),
  storageBucket: z4.string().optional(),
  contentType: z4.string(),
  fileSize: z4.number().int()
});
var LinkProductFileInputSchema = z4.object({
  productId: z4.string().uuid(),
  productFileId: z4.string().uuid(),
  externalProductId: z4.number().int()
  // B2BWave product ID
});
var ProductProductFileDtoSchema = z4.object({
  id: z4.string().uuid(),
  productId: z4.string().uuid(),
  productFileId: z4.string().uuid(),
  externalProductId: z4.number().int(),
  createdAt: z4.string().datetime()
});

// product/product-audit.schema.ts
import { z as z5 } from "zod";
var ProductChangeTypeSchema = z5.enum([
  "create",
  "status_change",
  "update_info",
  "update_image",
  "add_file",
  "remove_file",
  "add_variant",
  "update_variant",
  "remove_variant",
  "add_product_image",
  "remove_product_image",
  "reorder_product_images"
]);
var AuditUserSchema = z5.object({
  id: z5.string().uuid(),
  name: z5.string(),
  email: z5.string().email(),
  role: PortalUserRoleSchema
});
var ProductAuditEntryDtoSchema = z5.object({
  id: z5.string().uuid(),
  productId: z5.string().uuid(),
  changeType: ProductChangeTypeSchema,
  fieldName: z5.string().nullable(),
  oldValue: z5.string().nullable(),
  newValue: z5.string().nullable(),
  metadata: z5.record(z5.unknown()),
  createdAt: z5.string().datetime(),
  user: AuditUserSchema.nullable()
});
var ProductAuditQuerySchema = z5.object({
  limit: z5.coerce.number().int().min(1).max(100).default(25),
  offset: z5.coerce.number().int().min(0).default(0)
});
var ProductAuditResponseSchema = z5.object({
  productId: z5.string().uuid(),
  entries: z5.array(ProductAuditEntryDtoSchema),
  total: z5.number().int(),
  hasMore: z5.boolean()
});
var UpdateProductInfoInputSchema = z5.object({
  title: z5.string().min(1).max(500).optional(),
  description: z5.string().max(1e4).nullable().optional(),
  categoryId: z5.string().uuid().nullable().optional()
});
var UploadProductImageDataInputSchema = z5.object({
  imageData: z5.string(),
  // Base64 encoded image data
  imageFileName: z5.string().max(255),
  imageContentType: ImageContentTypeSchema
});
var ProductEditorDtoSchema = z5.object({
  id: z5.string().uuid(),
  name: z5.string(),
  email: z5.string().email(),
  role: PortalUserRoleSchema
});
var ProductEditorsResponseSchema = z5.object({
  productId: z5.string().uuid(),
  brandId: z5.string().uuid(),
  editors: z5.array(ProductEditorDtoSchema),
  totalEditors: z5.number().int()
});
var DbProductAuditLogSchema = z5.object({
  id: z5.string().uuid(),
  product_id: z5.string().uuid(),
  portal_user_id: z5.string().uuid().nullable(),
  change_type: ProductChangeTypeSchema,
  field_name: z5.string().nullable(),
  old_value: z5.string().nullable(),
  new_value: z5.string().nullable(),
  metadata: z5.record(z5.unknown()),
  created_at: z5.union([z5.string(), z5.date()])
});
var CreateDbProductAuditLogSchema = z5.object({
  id: z5.string().uuid().optional(),
  product_id: z5.string().uuid(),
  portal_user_id: z5.string().uuid().nullable().optional(),
  change_type: ProductChangeTypeSchema,
  field_name: z5.string().nullable().optional(),
  old_value: z5.string().nullable().optional(),
  new_value: z5.string().nullable().optional(),
  metadata: z5.record(z5.unknown()).optional(),
  created_at: z5.string().optional()
});

// product/brand-inventory.schema.ts
import { z as z6 } from "zod";
var BrandInventorySummaryDtoSchema = z6.object({
  brandId: z6.string().uuid(),
  brandName: z6.string(),
  brandLogo: z6.string().nullable(),
  brandDescription: z6.string().nullable(),
  productCount: z6.number().int(),
  totalOnHand: z6.number().int(),
  totalReserved: z6.number().int(),
  totalAvailable: z6.number().int()
});
var BrandInventorySummaryResponseSchema = z6.object({
  brands: z6.array(BrandInventorySummaryDtoSchema)
});
var ProductWithInventoryDtoSchema = z6.object({
  id: z6.string().uuid(),
  title: z6.string(),
  sku: z6.string().nullable(),
  thumbnail: z6.string().nullable(),
  status: z6.string(),
  locationCount: z6.number().int(),
  totalOnHand: z6.number().int(),
  totalReserved: z6.number().int(),
  totalAvailable: z6.number().int()
});
var BrandProductInventoryResponseSchema = z6.object({
  brandId: z6.string().uuid(),
  brandName: z6.string(),
  brandLogo: z6.string().nullable(),
  products: z6.array(ProductWithInventoryDtoSchema),
  total: z6.number().int(),
  page: z6.number().int(),
  pageSize: z6.number().int(),
  hasMore: z6.boolean()
});
export {
  AddProductImageInputSchema,
  AddProductVariantInputSchema,
  AddProductVariantResponseSchema,
  AdminProductListingDtoSchema,
  AdminProductListingFiltersSchema,
  AdminProductListingResponseSchema,
  AuditUserSchema,
  BrandInventorySummaryDtoSchema,
  BrandInventorySummaryResponseSchema,
  BrandProductInventoryResponseSchema,
  BulkCreateProductResultSchema,
  BulkCreateProductsInputSchema,
  BulkCreateProductsResponseSchema,
  BulkCreatedProductSummarySchema,
  CreateDbProductAuditLogSchema,
  CreateProductDocumentInputSchema,
  CreateProductImageInputSchema,
  CreateProductInputSchema,
  CreateProductVariantInputSchema,
  CreatedProductDtoSchema,
  CreatedProductVariantDtoSchema,
  DbProductAuditLogSchema,
  DeleteProductGalleryImageResponseSchema,
  DeleteProductVariantResponseSchema,
  FileSourceSchema,
  GENERAL_MERCHANDISE_CATEGORIES,
  ImageContentTypeSchema,
  ImageSourceSchema,
  LinkProductFileInputSchema,
  LinkedMetrcItemIdsResponseSchema,
  PRODUCT_REALMS,
  ProductAuditEntryDtoSchema,
  ProductAuditQuerySchema,
  ProductAuditResponseSchema,
  ProductChangeTypeSchema,
  ProductCreationErrorSchema,
  ProductEditorDtoSchema,
  ProductEditorsResponseSchema,
  ProductFileDtoSchema,
  ProductImageDtoSchema,
  ProductImagesListResponseSchema,
  ProductInventoryResponseSchema,
  ProductInventorySummarySchema,
  ProductLocationInventoryDtoSchema,
  ProductProductFileDtoSchema,
  ProductRealmSchema,
  ProductReservationDtoSchema,
  ProductReservationsResponseSchema,
  ProductStatusSchema,
  ProductTypeFilterSchema,
  ProductVariantDetailDtoSchema,
  ProductVariantsResponseSchema,
  ProductWithInventoryDtoSchema,
  REALM_COMPLIANCE_DEFAULTS,
  REALM_FULFILLMENT_DEFAULTS,
  RealmComplianceConfigSchema,
  RealmContextSchema,
  ReorderProductGalleryInputSchema,
  ReorderProductImagesInputSchema,
  SupplierProductListingDtoSchema,
  SupplierProductListingResponseSchema,
  UpdateFileStorageInfoInputSchema,
  UpdateProductImageInputSchema,
  UpdateProductInfoInputSchema,
  UpdateProductVariantInputSchema,
  UpdateProductVariantResponseSchema,
  UpdateStorageInfoInputSchema,
  UploadProductImageDataInputSchema,
  UpsertProductFileInputSchema,
  realmRequiresCompliance,
  resolveOrgBrandFulfillment
};
